import { useEffect, useMemo, useRef, useState } from 'react';

import { Drawer } from '@/design-system/primitives';
import {
  useAgentThreads,
  useCreateAgentThread,
} from '@/lib/api/clinicalAdminHooks';

import { ChatPanel } from './AgentScreen';

export interface ClinicalAgentContext {
  patientName?: string | null;
  patientEmail?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  ticketNumber?: string | null;
  ticketSubject?: string | null;
}

export function clinicalAgentDraft(context: ClinicalAgentContext): string {
  const patient = context.patientName?.trim();
  const email = context.patientEmail?.trim();
  const order = context.orderNumber?.trim() || context.orderId?.trim();
  const patientPhrase = patient
    ? `${patient}${email ? ` with email ${email}` : ''}`
    : email
      ? `with email ${email}`
      : 'currently in view';
  const ticket = context.ticketNumber
    ? ` This is support ticket ${context.ticketNumber}${
        context.ticketSubject ? `: ${context.ticketSubject}` : ''
      }.`
    : '';
  return `For patient ${patientPhrase}${order ? ` and order ${order}` : ''}.${ticket}\n\n`;
}

/** Context-aware access to the existing clinical-ops agent and its threads. */
export function ClinicalAgentDrawer({
  open,
  onClose,
  context,
}: {
  open: boolean;
  onClose: () => void;
  context: ClinicalAgentContext;
}) {
  const threads = useAgentThreads('', open);
  const create = useCreateAgentThread();
  const [threadId, setThreadId] = useState<string | null>(null);
  const wasOpen = useRef(false);
  const draft = useMemo(() => clinicalAgentDraft(context), [context]);

  const createThread = async () => {
    const result = await create.mutateAsync();
    setThreadId(result.thread_id);
  };

  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current) return;
    wasOpen.current = true;
    void createThread();
    // Opening is the user action that creates a fresh, context-seeded thread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Clinical AI"
      size="wide"
      layer="nested"
    >
      <div className="p-15 flex flex-col gap-12">
        <div className="flex flex-wrap items-center gap-8">
          <label className="type-label-sm text-ink-muted" htmlFor="clinical-agent-thread">
            Thread
          </label>
          <select
            id="clinical-agent-thread"
            value={threadId ?? ''}
            onChange={(event) => setThreadId(event.target.value || null)}
            className="min-w-[18rem] flex-1 type-label-sm px-10 py-8 rounded border border-line bg-raised"
          >
            <option value="">Creating a context thread…</option>
            {(Array.isArray(threads.data) ? threads.data : []).map((thread) => (
              <option key={thread.id} value={thread.id}>
                {thread.title || 'Untitled'}
                {thread.pending_count ? ` · ${thread.pending_count} pending` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void createThread()}
            disabled={create.isPending}
            className="type-label-sm px-10 py-8 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            + New thread
          </button>
        </div>

        <div className="rounded border border-line bg-sunken/40 px-10 py-8 type-label-sm text-ink-muted">
          A patient/order reference has been drafted below. Review or extend it before sending.
        </div>

        {threadId ? (
          <ChatPanel threadId={threadId} initialDraft={draft} />
        ) : create.isError ? (
          <div className="type-label-sm text-danger">
            Could not start the clinical AI thread. Try again.
          </div>
        ) : (
          <div className="type-label-sm text-ink-muted py-24">Starting thread…</div>
        )}
      </div>
    </Drawer>
  );
}
